<!--
// app/components/glossary/GlossaryDrawer.vue
-->
<script setup lang="ts">
import GlossaryCategoryAccordion from "./GlossaryCategoryAccordion.vue";
import KeepPinIcon from "~/components/Shared/icons/KeepPinIcon.vue";
import CloseIcon from "~/components/Shared/icons/CloseIcon.vue";
import SearchIcon from "~/components/Shared/icons/SearchIcon.vue";

type Concept = {
  id: string;
  title: string;
  definition: string;
};

type Category = {
  title: string;
  concepts: Concept[];
};

type Props = {
  categories: Category[];
  searchPlaceholder?: string;
  pinLabel?: string;
  unpinLabel?: string;
};

const props = defineProps<Props>();
const model = defineModel<boolean>({ required: true });
const { $t } = useI18n();

const emit = defineEmits<{
  conceptClicked: [conceptId: string];
}>();

const isMobile = ref(false);

// Search functionality
const searchQuery = ref("");
const highlightedConceptId = ref<string | undefined>();

// Pin functionality with localStorage persistence
const isPinned = ref(false);

const updateIsMobile = () => {
  isMobile.value = window.innerWidth < 640;
};

onMounted(() => {
  updateIsMobile();
  window.addEventListener("resize", updateIsMobile);

  const stored = localStorage.getItem("mercato_glossary_pinned");
  isPinned.value = stored === "true";
  if (isMobile.value && isPinned.value) {
    isPinned.value = false;
    localStorage.removeItem("mercato_glossary_pinned");
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", updateIsMobile);
  document.body.style.overflow = "";
});

watch(isPinned, newValue => {
  localStorage.setItem("mercato_glossary_pinned", String(newValue));
});

watch(isMobile, nowMobile => {
  if (nowMobile && isPinned.value) {
    isPinned.value = false;
    localStorage.removeItem("mercato_glossary_pinned");
  }
});

// Filtered categories based on search
const filteredCategories = computed(() => {
  if (!searchQuery.value.trim()) {
    return props.categories;
  }

  const query = searchQuery.value.toLowerCase();

  return props.categories
    .map(category => ({
      ...category,
      concepts: category.concepts.filter(
        concept =>
          concept.title.toLowerCase().includes(query) || concept.definition.toLowerCase().includes(query)
      ),
    }))
    .filter(category => category.concepts.length > 0);
});

// Close drawer on Esc key
const onKeyDown = (e: KeyboardEvent) => {
  if (e.key === "Escape" && !isPinned.value) {
    model.value = false;
  }
};

onMounted(() => {
  window.addEventListener("keydown", onKeyDown);
});

onUnmounted(() => {
  window.removeEventListener("keydown", onKeyDown);
});

// Clear highlight when drawer closes
watch(model, isOpen => {
  if (!isOpen) {
    highlightedConceptId.value = undefined;
    if (typeof document !== "undefined") {
      document.body.style.overflow = "";
    }
    searchQuery.value = "";
  } else if (isMobile.value && typeof document !== "undefined") {
    document.body.style.overflow = "hidden";
  }
});

onUnmounted(() => {
  if (typeof document !== "undefined") {
    document.body.style.overflow = "";
  }
});

// Handle backdrop click
const onBackdropClick = () => {
  if (!isPinned.value) {
    model.value = false;
  }
};

// Scroll to concept (only within drawer, not the page)
const scrollToConcept = (conceptId: string) => {
  highlightedConceptId.value = conceptId;
  emit("conceptClicked", conceptId);

  nextTick(() => {
    // Clear search so the concept list is fully visible when opened again
    searchQuery.value = "";

    const element = document.getElementById(conceptId);
    const drawerContent = document.querySelector(".glossary-drawer-content");

    if (element && drawerContent) {
      // Get element position relative to drawer content
      const elementRect = element.getBoundingClientRect();
      const drawerRect = drawerContent.getBoundingClientRect();
      const offset = elementRect.top - drawerRect.top + drawerContent.scrollTop;

      // Scroll only the drawer content, not the page
      drawerContent.scrollTo({
        top: offset - 100, // Offset to center better
        behavior: "smooth",
      });

      // Remove highlight after animation
      setTimeout(() => {
        highlightedConceptId.value = undefined;
      }, 2000);
    }
  });
};

// Expose scrollToConcept for parent component
defineExpose({ scrollToConcept });

// Clear search
const clearSearch = () => {
  searchQuery.value = "";
};
</script>

<template>
  <Teleport to="body">
    <!-- Backdrop (subtle, only visible when not pinned) -->
    <Transition name="fade">
      <div
        v-if="model && (!isPinned || isMobile)"
        class="fixed inset-0 bg-black/50 z-[1999]"
        @click="onBackdropClick"
      ></div>
    </Transition>

    <!-- Drawer -->
    <Transition name="slide">
      <div
        v-if="model"
        class="fixed top-0 ltr:right-0 rtl:left-0 h-full bg-gray-900 border-s-4 border-mercato shadow-2xl z-[2000] flex flex-col w-full sm:w-[400px] md:w-[420px] overflow-hidden"
      >
        <!-- Header -->
        <div class="flex-shrink-0 p-4 border-b border-gray-700">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-white font-bold text-xl">
              <slot name="title"></slot>
            </h2>

            <div class="flex items-center gap-2">
              <!-- Close on mobile -->
              <button
                v-if="isMobile"
                type="button"
                class="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors duration-200"
                @click="model = false"
              >
                <CloseIcon width="18" height="18" />
              </button>

              <!-- Pin button (desktop only) -->
              <button
                v-else
                type="button"
                :title="isPinned ? unpinLabel : pinLabel"
                class="p-2 rounded-lg transition-colors duration-200 hover:bg-gray-800"
                :class="isPinned ? 'text-mercato hover:text-gray-400' : 'text-gray-400 hover:text-mercato '"
                @click="isPinned = !isPinned"
              >
                <KeepPinIcon :is-pinned="isPinned" />
              </button>
            </div>
          </div>

          <!-- Search input -->
          <div class="relative">
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="searchPlaceholder"
              class="w-full px-4 py-2 pr-10 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-mercato focus:outline-none focus:ring-2 focus:ring-mercato/50 transition-colors duration-200"
            />
            <button
              v-if="searchQuery"
              @click="clearSearch"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors duration-200"
            >
              <CloseIcon width="12" height="12" />
            </button>
            <SearchIcon
              v-else
              width="18"
              height="18"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
          </div>
        </div>

        <!-- Content -->
        <div class="glossary-drawer-content flex-1 overflow-y-auto p-4">
          <div v-if="filteredCategories.length === 0" class="text-center py-8 text-gray-400">
            <p>{{ $t("mercato.keyConcepts.emptyState") }}</p>
          </div>

          <glossary-category-accordion
            v-for="(category, index) in filteredCategories"
            :key="index"
            :category-title="category.title"
            :concepts="category.concepts"
            :highlighted-concept-id="highlightedConceptId"
            :default-open="searchQuery.length > 0 || index === 0"
          />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Fade transition for backdrop */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Slide transition for drawer */
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease-in-out;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

.slide-enter-from:where([dir="rtl"], [dir="rtl"] *),
.slide-leave-to:where([dir="rtl"], [dir="rtl"] *) {
  transform: translateX(-100%);
}
</style>
